import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Mail, AtSign, Phone, MapPin, FileText } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

export const Footer = () => {
  const { t } = useLanguage();

  const rows = [
    { icon: Mail, label: t.footer.emailLabel, value: "cooperativafilia@gmail.com", href: "mailto:cooperativafilia@gmail.com", testid: "footer-email-link" },
    { icon: AtSign, label: t.footer.pecLabel, value: "cooperativafilia@pec.it", href: "mailto:cooperativafilia@pec.it", testid: "footer-pec-link" },
    { icon: Phone, label: t.footer.phoneLabel, value: "+39 081 1966 3532", href: "tel:+3908119663532", testid: "footer-phone-link" },
    { icon: MapPin, label: t.footer.hqLabel, value: t.footer.hqValue, testid: "footer-hq" },
    { icon: FileText, label: t.footer.vatLabel, value: t.footer.vatValue, testid: "footer-vat" },
  ];

  return (
    <footer data-testid="site-footer" className="bg-ink text-cream">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
        <div className="grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-6">
            <motion.h2
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="font-serif font-medium tracking-tight text-5xl sm:text-6xl lg:text-7xl leading-[1.02]"
            >
              {t.footer.title}
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, delay: 0.15 }}
              className="mt-6 max-w-md text-base md:text-lg leading-relaxed text-cream/70"
            >
              {t.footer.sub}
            </motion.p>
          </div>

          <div className="lg:col-span-5 lg:col-start-8 flex flex-col justify-center gap-2">
            {rows.map((r, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className="flex items-center gap-4 py-4 border-b border-cream/10 last:border-0"
              >
                <span className="w-10 h-10 rounded-full bg-cream/10 flex items-center justify-center shrink-0">
                  <r.icon size={17} strokeWidth={1.5} className="text-terra" />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-widest text-cream/50">{r.label}</p>
                  {r.href ? (
                    <a data-testid={r.testid} href={r.href} className="text-base md:text-lg font-medium text-cream hover:text-terra transition-colors duration-200">
                      {r.value}
                    </a>
                  ) : (
                    <p data-testid={r.testid} className="text-base md:text-lg font-medium text-cream">{r.value}</p>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      <div className="border-t border-cream/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-7 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
          <p className="font-serif text-2xl font-medium">
            Filia<span className="text-terra">.</span>
          </p>
          <div className="flex items-center gap-4">
            <p className="text-xs text-cream/50">{t.footer.tagline}</p>
            <Link to="/privacy" data-testid="footer-privacy-link" className="text-xs text-cream/50 hover:text-terra transition-colors duration-200">
              Privacy Policy
            </Link>
          </div>
          <p data-testid="footer-rights" className="text-xs text-cream/50">{t.footer.rights}</p>
        </div>
      </div>
    </footer>
  );
};
